import { round } from './math.util'

/**
 * Narrow symbols that en-US shares across multiple currencies, so they don't
 * identify the currency on their own — e.g. "$" is AUD/CAD/NZD/SGD/HKD/MXN,
 * "¥" is JPY/CNY, "kr" is SEK/NOK/DKK/ISK, "Rs" is PKR/LKR/NPR. For these we
 * keep the disambiguated "symbol" form (A$, CA$, CN¥, SEK …) instead of a
 * glyph the reader can't pin to a country.
 */
const AMBIGUOUS_NARROW_SYMBOLS = new Set(['$', '¥', 'kr', 'Rs'])

const resolveCurrencyPart = (currency, currencyDisplay) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay })
    .formatToParts(0)
    .find((part) => part.type === 'currency')?.value ?? currency

/**
 * Canonical currency-symbol resolver for the whole platform.
 *
 * Uses Intl with no hand-maintained map. Prefers the local/narrow glyph
 * ("narrowSymbol") so e.g. THB → "฿", ZAR → "R", GBP → "£". But when that
 * glyph is one en-US shares across many currencies (see
 * AMBIGUOUS_NARROW_SYMBOLS), it falls back to the disambiguated "symbol" form
 * so the currency stays identifiable — e.g. AUD → "A$", CAD → "CA$",
 * CNY → "CN¥", SGD → "SGD", USD → "$". Falls back to the raw code for
 * unknown/invalid codes, and '' when none is provided. This is the single
 * source of truth — `string.util` and the backend re-use it so Core and
 * member-facing surfaces never disagree.
 */
export const getCurrencySymbol = (currency) => {
  if (!currency) {
    return ''
  }
  try {
    const narrow = resolveCurrencyPart(currency, 'narrowSymbol')
    return AMBIGUOUS_NARROW_SYMBOLS.has(narrow) ? resolveCurrencyPart(currency, 'symbol') : narrow
  } catch {
    return currency
  }
}

export const formatValueOnReceipt = (value, currency) => {
  const sign = value < 0 ? '-' : ''
  const currencySymbol = getCurrencySymbol(currency)
  const amount = Math.abs(round(value, 2)).toFixed(2)
  // Keep the established " $ 12.34" spacing, but drop the symbol slot entirely
  // when no currency is resolved (loading / unscoped site) so we never render a
  // stray double space.
  return currencySymbol ? `${sign} ${currencySymbol} ${amount}` : `${sign} ${amount}`
}

/**
 * Format a numeric amount as a localized currency string, guarding against an
 * invalid ISO 4217 code. `Intl.NumberFormat` throws a `RangeError` on an
 * unknown currency, and the site currency can be empty/misconfigured (e.g. ''
 * while site data loads), so we never call Intl unguarded: an empty code falls
 * back to USD, and any code Intl rejects falls back to the resolved symbol +
 * the plain number rather than crashing the render. `options` is forwarded to
 * `Intl.NumberFormat` (e.g. `{ maximumFractionDigits: 0 }`).
 */
export const formatCurrencyValue = (value, currency, options = {}) => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      ...options,
    }).format(value)
  } catch {
    const symbol = getCurrencySymbol(currency) || '$'
    const digits = options.maximumFractionDigits ?? 2
    return `${symbol}${value.toFixed(digits)}`
  }
}
